/**
 * @copyright WaterStreet. All rights reserved.
 */

import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnInit,
	signal,
	ViewChild,
	WritableSignal
} from '@angular/core';
import {
	ExtendedCustomControlDirective
} from '@entity/directives/extended-custom-control.directive';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	InputMask
} from 'primeng/inputmask';

@Component({
	selector: 'custom-masked-input',
	templateUrl: './custom-masked-input.component.html',
	styleUrls: [
		'./custom-masked-input.component.scss'
	]
})

/**
 * A component representing an instance of a Custom Masked Input.
 * https://ngx-formly.github.io/ngx-formly/guide
 *
 * @export
 * @class CustomMaskedInputComponent
 * @extends {ExtendedCustomControlDirective}
 * @implements {AfterViewInit}
 * @implements {OnInit}
 */
export class CustomMaskedInputComponent
	extends ExtendedCustomControlDirective
	implements OnInit, AfterViewInit
{
	/** Initializes a new instance of the CustomMaskedInputComponent.
	 *
	 * @param {ChangeDetectorRef} changeDetector
	 * The change detector reference for this component.
	 * @memberof CustomMaskedInputComponent
	 */
	public constructor(
		public changeDetector: ChangeDetectorRef)
	{
		super(changeDetector);
	}

	/**
	 * Gets or sets masked input control.
	 *
	 * @type {InputMask}
	 * @memberof CustomMaskedInputComponent
	 */
	@ViewChild('maskedInput')
	public maskedInput: InputMask;

	/**
	 * Gets or sets the sensitive input control.
	 *
	 * @type {InputMask}
	 * @memberof CustomMaskedInputComponent
	 */
	@ViewChild('sensitiveInput')
	public sensitiveInput: InputMask;

	/**
	 * Gets or sets the toggle visibility button element reference.
	 *
	 * @type {ElementRef<HTMLButtonElement>}
	 * @memberof CustomMaskedInputComponent
	 */
	@ViewChild('toggleButton')
	public toggleButton: ElementRef<HTMLButtonElement>;

	/**
	 * Gets or sets the visibility state of the sensitive value.
	 *
	 * @type {WritableSignal<boolean>}
	 * @memberof CustomMaskedInputComponent
	 */
	public isRevealed: WritableSignal<boolean> =
		signal<boolean>(false);

	/**
	 * Gets or sets the state that indicates if the field is
	 * marked as sensitive.
	 *
	 * @type {boolean}
	 * @memberof CustomMaskedInputComponent
	 */
	public isSensitive: boolean = false;

	/**
	 * Gets the state that indicates if users can reveal the field value.
	 *
	 * @readonly
	 * @type {boolean}
	 * @memberof CustomMaskedInputComponent
	 */
	public get canToggleVisibility(): boolean
	{
		return this.field.props.securityAccessPolicy?.rights?.read === true
			&& (!this.isDisabled
				|| !this.isEmpty);
	}

	/**
	 * Gets a boolean that indicates if the field
	 * is disabled or read-only.
	 *
	 * @readonly
	 * @type {boolean}
	 * @memberof CustomMaskedInputComponent
	 */
	public get isDisabled(): boolean
	{
		return this.formControl.disabled === true
			|| this.field.props.disabled === true
			|| this.field.props.readonly === true;
	}

	/**
	 * Gets a boolean that indicates if the field is empty.
	 *
	 * @readonly
	 * @type {boolean}
	 * @memberof CustomMaskedInputComponent
	 */
	public get isEmpty(): boolean
	{
		return AnyHelper.isNullOrEmpty(
			this.getUnderlyingValue());
	}

	/**
	 * Gets the state defining if the sensitive input mask
	 * should be re-masked on blur.
	 *
	 * @readonly
	 * @type {boolean}
	 * @memberof CustomMaskedInputComponent
	 */
	public get remaskOnBlur(): boolean
	{
		return AnyHelper.isNull(
			this.field.props.remaskOnBlur)
				|| this.field.props.remaskOnBlur === true;
	}

	/**
	 * Gets or sets a value indicating whether the toggle button
	 * mouse-down event is currently active. Used to distinguish
	 * a button click from Tab-key navigation in blur handling.
	 *
	 * @type {boolean}
	 * @memberof CustomMaskedInputComponent
	 */
	private isToggleButtonMouseDown: boolean = false;

	/**
	 * Gets the list of mask tokens used to identify characters in the mask.
	 *
	 * @readonly
	 * @type {string[]}
	 * @memberof CustomMaskedInputComponent
	 */
	private readonly maskTokens: string[] =
		[
			FormlyConstants
				.maskedInputMaskCharacters
				.numeric,
			FormlyConstants
				.maskedInputMaskCharacters
				.alphabetic,
			FormlyConstants
				.maskedInputMaskCharacters
				.alphanumeric
		];

	/**
	 * On component initialization event.
	 * Sets static configuration properties derived from field props.
	 *
	 * @memberof CustomMaskedInputComponent
	 */
	public override ngOnInit(): void
	{
		this.isSensitive =
			this.field.props.sensitive === true;

		super.ngOnInit();
	}

	/**
	 * On after view initialization event.
	 * Sets the hidden input value for this masked input to match the data
	 * found on load. Once this is hooked in, all further form interactions
	 * are the same as other inputs.
	 *
	 * @memberof CustomMaskedInputComponent
	 */
	public ngAfterViewInit(): void
	{
		if (this.isSensitive)
		{
			this.isRevealed.set(
				this.isEmpty
					&& !this.isDisabled);

			return;
		}

		// Work around for not using NGModel for the masked input
		// and using the form control directly as formly requires.
		this.maskedInput.inputViewChild.nativeElement.value =
			this.maskedInput.value;

		const existingValue: string = this.field.formControl.value;
		this.field.formControl.reset();
		this.field.formControl.setValue(existingValue);
		this.validateControl();
	}

	/**
	 * Toggles the sensitive value visibility.
	 *
	 * @memberof CustomMaskedInputComponent
	 */
	public toggleVisibility(): void
	{
		this.isToggleButtonMouseDown = false;

		if (!this.canToggleVisibility)
		{
			return;
		}

		this.isRevealed.set(
			!this.isRevealed());
	}

	/**
	 * Marks that the toggle button's mouse-down event has fired.
	 * This allows blur handling to distinguish a mouse click on
	 * the toggle button from Tab-key navigation to it.
	 *
	 * @memberof CustomMaskedInputComponent
	 */
	public onToggleMouseDown(): void
	{
		this.isToggleButtonMouseDown = true;
	}

	/**
	 * Handles blur event for the sensitive field.
	 *
	 * @param {FocusEvent} event
	 * The blur event.
	 * @memberof CustomMaskedInputComponent
	 */
	public handleBlur(
		event: FocusEvent): void
	{
		const clickedElement: HTMLElement =
			event.relatedTarget as HTMLElement;

		const isToggleButtonClick: boolean =
			clickedElement === this.toggleButton?.nativeElement
				&& this.isToggleButtonMouseDown;

		this.isToggleButtonMouseDown = false;

		if (isToggleButtonClick
			|| !this.remaskOnBlur
			|| !this.isRevealed())
		{
			return;
		}

		this.isRevealed.set(false);
	}

	/**
	 * Gets the value of the underlying form control.
	 *
	 * @returns {string}
	 * The underlying value.
	 * @memberof CustomMaskedInputComponent
	 */
	public getUnderlyingValue(): string
	{
		if (this.formControl.value == null)
		{
			return AppConstants.empty;
		}

		return String(this.formControl.value);
	}

	/**
	 * Gets the fully masked display value for the underlying value.
	 *
	 * @returns {string}
	 * The fully masked display value.
	 * @memberof CustomMaskedInputComponent
	 */
	public getMaskedValue(): string
	{
		return this.buildMaskedDisplayValue(
			this.getUnderlyingValue());
	}

	/**
	 * Builds a fully masked display value from the underlying value.
	 *
	 * @param {string} underlyingValue
	 * The underlying input value.
	 * @returns {string}
	 * The fully masked display value.
	 * @memberof CustomMaskedInputComponent
	 */
	public buildMaskedDisplayValue(
		underlyingValue: string): string
	{
		if (AnyHelper
			.isNullOrEmpty(
				underlyingValue))
		{
			return AppConstants.empty;
		}

		const maskCharacter: string =
			AppConstants.characters.asterisk;

		const configuredMask: string =
			this.field.props.mask;

		const showLastDigits: number =
			this.field.props.showLastDigits ?? 0;

		if (AnyHelper
			.isNullOrEmpty(
				configuredMask))
		{
			const maskedLength: number =
				Math.max(
					0,
					underlyingValue.length
						- showLastDigits);

			return maskCharacter
				.repeat(
					maskedLength)
				+ underlyingValue
					.slice(
						maskedLength);
		}

		const unmaskedCharacters: string[] =
			underlyingValue
				.replace(
					/[^a-zA-Z0-9]/g,
					AppConstants.empty)
				.split(
					AppConstants.empty);

		const revealFromIndex: number =
			Math.max(
				0,
				unmaskedCharacters.length
					- showLastDigits);

		let maskedValue: string = AppConstants.empty;
		let currentValueIndex: number = 0;

		for (const maskChar of configuredMask)
		{
			if (currentValueIndex
				>= unmaskedCharacters.length)
			{
				break;
			}

			const isMaskToken: boolean =
				this.maskTokens
					.includes(
						maskChar);

			if (isMaskToken)
			{
				maskedValue +=
					currentValueIndex >= revealFromIndex
						? unmaskedCharacters[currentValueIndex]
						: maskCharacter;

				currentValueIndex++;
			}
			else if (maskChar
				!== FormlyConstants
					.maskedInputMaskCharacters
					.optionalDelimiter)
			{
				maskedValue += maskChar;
			}
		}

		return maskedValue;
	}
}